'use client';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import type { SafeType } from '@benzinga/safe-await';
import { Authentication, AuthMode } from '@benzinga/session';
import i18n, { LocaleType } from '@benzinga/translate';
import { getGlobalConfigByLocale } from '@benzinga/identity-ui';
import { MainLogo } from '@benzinga/logos-ui';

import { Login } from '../../components/Login';
import { Register } from '../../components/Register';
import { useSearchParams } from '../../hooks/useSearchParams';

import type { AuthAction, AuthContainerV2Props } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useAuthTracking } from '../hooks/useAuthTracking';
import { resolveReturnUrl } from '../utils/resolveReturnUrl';
import { emitAuthSuccess } from '../utils/emitAuthSuccess';
import { SmsVerifySurface } from './SmsVerifySurface';
import { sanitizeHTML } from '@benzinga/frontend-utils';

import styles from './surface.module.css';
import { Spinner } from '@benzinga/core-ui';

/**
 * Full login/register form layout. Used by /login and by modal wrappers in pro/research.
 * On `?social=success`: refresh session → emit auth:success → redirect to resolved returnUrl.
 * On email success: emit auth:success → redirect (unless `redirectOnSuccess=false`).
 */

const DefaultIterationContent: React.FC = () => {
  const { t } = useTranslation(['auth', 'common']);
  return (
    <div className={styles.defaultIterationContent}>
      <Link href="/" prefetch={false}>
        <MainLogo
          logoVariant={getGlobalConfigByLocale(i18n.language as LocaleType)?.logoVariant || 'default'}
          variant="light"
        />
      </Link>
      <h3 className="text-4xl text-white mb-4">{t('Auth.AuthModal.login.get-unlimited-access')}</h3>
      <p className="text-white">
        <span dangerouslySetInnerHTML={{ __html: sanitizeHTML(t('Auth.AuthModal.login.come-see', { ns: 'auth' })) }} />{' '}
      </p>
      <p className="max-w-[400px]">
        <a className="underline text-blue-500" href="/reports/TSLA" rel="noopener noreferrer" target="_blank">
          {t('Auth.AuthModal.Benefits.in-depth-reports', { ns: 'auth' })}
        </a>
        ,{' '}
        <a className="underline text-blue-500" href="/analyst-stock-ratings" rel="noopener noreferrer" target="_blank">
          {t('Auth.AuthModal.Benefits.analyst-ratings', { ns: 'auth' })}
        </a>
        ,{' '}
        <a className="underline text-blue-500" href="/earnings" rel="noopener noreferrer" target="_blank">
          {t('Auth.AuthModal.Benefits.earnings', { ns: 'auth' })}
        </a>
        , {t('Auth.AuthModal.Benefits.and-more', { ns: 'auth' })}{' '}
        {t('Auth.AuthModal.Benefits.or-return-to', { ns: 'auth' })}{' '}
        <a className="underline text-blue-500" href="/" rel="noopener noreferrer" target="_blank">
          Benzinga.com
        </a>
      </p>
    </div>
  );
};

export const PageSurface: React.FC<AuthContainerV2Props> = ({
  email,
  fallbackReturnUrl,
  initialMode,
  onSuccess,
  placement,
  redirectOnSuccess = true,
}) => {
  const searchParams = useSearchParams();
  const auth = useAuth();
  const { trackSocialReturn } = useAuthTracking({ surface: 'page' });

  const isSocialReturn = searchParams.get('social') === 'success';

  type PageMode = 'login' | 'register' | 'sms-verify' | 'complete';
  const initial = ((searchParams.get('action') as 'login' | 'register' | null) ?? initialMode ?? 'login') as PageMode;
  const isIframe = searchParams.get('iframe') === 'true';
  const [mode, setMode] = useState<PageMode>(initial);
  const [socialReturnError, setSocialReturnError] = useState<string | null>(null);

  // Carried across the register → sms-verify transition.
  const [pendingAuth, setPendingAuth] = useState<SafeType<Authentication> | null>(null);
  const [pendingPhone, setPendingPhone] = useState<string>('');

  const currentHost = typeof window !== 'undefined' ? window.location.hostname : '';
  const referrer = (typeof document !== 'undefined' && document.referrer) || fallbackReturnUrl || '';

  const getReturnUrl = useCallback(
    (action: 'login' | 'register') => resolveReturnUrl({ currentHost, mode: action, referrer, searchParams }).url,
    [currentHost, referrer, searchParams],
  );

  // Wraps the final destination so OAuth lands us back on /login?social=success&next=<final>.
  const buildSocialCallbackUrl = useCallback(() => {
    const socialMode = mode === 'register' ? 'register' : 'login';
    const finalUrl = getReturnUrl(socialMode);
    if (typeof window === 'undefined') return finalUrl;
    const u = new URL('/login', `${window.location.protocol}//${currentHost}`);
    u.searchParams.set('social', 'success');
    u.searchParams.set('next', finalUrl);
    return u.toString();
  }, [currentHost, getReturnUrl, mode]);

  // Social return → finalize OAuth, track, redirect.
  useEffect(() => {
    if (!isSocialReturn) return;
    let cancelled = false;
    void (async () => {
      const result = await auth.handleSocialReturn();
      if (cancelled) return;
      if (result.ok && result.ok.user && result.ok.user.accessType !== 'anonymous') {
        const detail = await trackSocialReturn(searchParams, result);
        if (detail) onSuccess?.(detail, result);
        if (redirectOnSuccess && typeof window !== 'undefined') {
          window.open(getReturnUrl('login'), '_top');
        }
      }
      if (result.err) {
        setSocialReturnError('Social authentication failed. Please try again later.');
        console.error('Social return failed', result.err);
      }
    })();
    return () => {
      cancelled = true;
      setSocialReturnError(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSocialReturn]);

  const handleEmailSuccess = useCallback(
    (authResult: SafeType<Authentication>, authFor: string) => {
      const user = authResult?.ok?.user;
      if (!user || user.accessType === 'anonymous') return;

      const action = authFor === 'register' ? 'register' : 'login';
      const detail = {
        action: action as AuthAction,
        isNewUser: action === 'register',
        method: 'email' as const,
        surface: 'page' as const,
        userId: user.id ? String(user.id) : undefined,
      };

      emitAuthSuccess(detail);
      onSuccess?.(detail, authResult);

      const returnUrl = getReturnUrl(action);

      if (isIframe) {
        window.parent.postMessage({ next: returnUrl, type: `${action}_succeed`, user }, '*');
      } else if (redirectOnSuccess && typeof window !== 'undefined') {
        window.open(returnUrl, '_top');
      }
    },
    [getReturnUrl, onSuccess, redirectOnSuccess, isIframe],
  );

  const handleSocialClicked = useCallback(
    (socialPath: string) => {
      if (typeof window === 'undefined') return;
      const callback = buildSocialCallbackUrl();
      const url = `https://accounts.benzinga.com/oauth/login/${socialPath}/?embedded=false&next=${encodeURIComponent(callback)}`;
      window.open(url, '_top');
    },
    [buildSocialCallbackUrl],
  );

  const handleSmsVerified = useCallback(
    (verifiedAuth: SafeType<Authentication>) => handleEmailSuccess(verifiedAuth, 'register'),
    [handleEmailSuccess],
  );

  // Hide form during social-return finalization to prevent flash.
  if (isSocialReturn) {
    return (
      <div data-state="social-return" data-surface="page">
        {socialReturnError ? (
          <div className="text-red-500">{socialReturnError}</div>
        ) : (
          <Spinner text="Processing social return...please wait." />
        )}
      </div>
    );
  }

  const setAuthMode = (next: AuthMode | string | string[]) => {
    if (next === 'login' || next === 'register' || next === 'sms-verify' || next === 'complete') {
      setMode(next);
    }
  };

  if (mode === 'sms-verify') {
    return (
      <div data-mode="sms-verify" data-placement={placement} data-surface="page">
        <SmsVerifySurface
          authentication={pendingAuth}
          onVerified={handleSmsVerified}
          parentSurface="page"
          phoneNumber={pendingPhone}
        />
      </div>
    );
  }

  // Transitional state Register may set just before redirect fires.
  if (mode === 'complete') {
    return <div data-mode="complete" data-placement={placement} data-surface="page" />;
  }

  if (mode === 'register') {
    return (
      <div className={styles.pageSurfaceWrapper} data-mode="register" data-placement={placement} data-surface="page">
        <DefaultIterationContent />
        <Register
          initialEmail={email}
          onRegister={handleEmailSuccess}
          onSocialClicked={handleSocialClicked}
          setAuthentication={setPendingAuth}
          setAuthMode={setAuthMode}
          setPhoneNumber={setPendingPhone}
        />
      </div>
    );
  }

  return (
    <div className={styles.pageSurfaceWrapper} data-mode="login" data-placement={placement} data-surface="page">
      <DefaultIterationContent />
      <Login onLogin={handleEmailSuccess} onSocialClicked={handleSocialClicked} setAuthMode={setAuthMode} />
    </div>
  );
};

PageSurface.displayName = 'PageSurface';
